import React, { useContext, useEffect, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import { RequestInstance } from '../../types/OrderStatus';
import { formatDateWithTZ, userFacingDateTimeFormat } from '../../../utils/timezone';
import { categoryColorMapping } from '../../utils/categoryColors';
import UserContext from '../../../contexts/UserContext';
import OrderCommentsSection from './OrderCommentsSection';
import { listComments } from '../../services/Comments';
import { cancelOrder, fetchOrdersFromOrderId } from '../../services/OrderStatus';
import sendToast from '../../../utils/Toast';
import { formatOrdersForTableView, getOrderNumber } from '../../../utils';
import { settleAll } from '../../utils/async';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import CloseAssociatedOrdersModal from './CloseAssociatedOrdersModal';
import { getOrderDisplayTotal } from '../../utils/paymentStatus';
import './style.less';

interface Props {
    order: RequestInstance;
    facilityTimezone: string;
    onOpen: (order: RequestInstance) => void;
    onCancelled?: () => void;
}

const STATUS_LABEL: Record<string, string> = { Open: 'Open', Accepted: 'Accepted', Closed: 'Closed' };

const initialsOf = (name?: string) =>
    (name || '')
        .split(' ')
        .filter(Boolean)
        .map((p) => p[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

const withHash = (color: string) => (color.startsWith('#') ? color : `#${color}`);

const OrderStatusCard: React.FC<Props> = ({ order, facilityTimezone, onOpen, onCancelled }) => {
    const [commentsOpen, setCommentsOpen] = useState(false);
    const [commentCount, setCommentCount] = useState<number | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [checkingAssociation, setCheckingAssociation] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [associatedOrders, setAssociatedOrders] = useState<RequestInstance[]>([]);
    const [openCloseAssociatedOrdersModal, setOpenCloseAssociatedOrdersModal] = useState(false);
    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const activeResident = listOfResidents[activeRegistrantsIndexes[0]];
    const hasComments = Boolean(order.Service?.defaults?.commentsEnabled);
    // `order` here is display-formatted upstream by formatOrdersForTableView, which
    // turns a missing OrderId into the literal string '-'. Treat that sentinel the
    // same as "no grouping id" instead of fetching/cancelling against a fake key.
    const hasAssociationKey = Boolean(order.OrderId) && order.OrderId !== '-';

    useEffect(() => {
        if (!hasComments) return;
        let cancelled = false;
        (async () => {
            try {
                // registrantId is sent even though comments/list currently ignores it for
                // requestInstance targets (its "approved + caller's own pending" widening is
                // inside the postId branch). The expanded thread below passes it, so sending
                // it here keeps the badge querying exactly what the thread queries — if that
                // widening is ever extended to order threads, the two can't drift apart.
                const res = await listComments({
                    facilityId: activeResident.Facility,
                    registrantId: String(activeResident._id),
                    requestInstanceId: String(order._id),
                    pageNo: 1,
                    pageSize: 1,
                });
                if (!cancelled) setCommentCount(res.TotRecords ?? 0);
            } catch {
                // count badge is a nice-to-have; leave blank on failure rather than surfacing a toast per card
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasComments, order._id, activeResident.Facility, activeResident._id]);

    const statusClass = (order.Status || 'open').toLowerCase();
    const deptColor = withHash(
        order.Department_Color
        || categoryColorMapping[order.Category as keyof typeof categoryColorMapping]
        || '#1f3566',
    );
    const addons = order.ServiceAddOns || [];
    const externalCost = Number(order.Service?.defaults?.externalCost || order.ExternalCost || 0);
    const total = getOrderDisplayTotal(order, addons.reduce((acc, addon) => acc + Number(addon.itemCost || 0), 0) + externalCost);
    const scheduled = order.ScheduledTime && order.ScheduledTime.startDate;
    const acceptedByName = order.AcceptedByName && order.AcceptedByName !== '-' ? order.AcceptedByName : '';
    const closedByLabel = order.Status === 'Closed'
        ? (order.ClosedByResident ? 'Canceled by resident' : order.ClosedByName)
        : '';
    const canCancel = order.Status === 'Open' || order.Status === 'Accepted';

    const openCancelConfirm = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!hasAssociationKey) {
            setShowCancelConfirm(true);
            return;
        }
        // Fetch associations lazily (only when the resident actually intends to
        // cancel) instead of on every card mount, and decide which confirmation
        // modal to show only once the fetch has actually settled — cancelling
        // before this resolves must not be treated as "no siblings".
        setCheckingAssociation(true);
        try {
            const response = await fetchOrdersFromOrderId({
                facilityId: activeResident.Facility,
                statusArr: ['Open', 'Accepted'],
                residentId: String(activeResident._id),
                OrderId: order.OrderId,
            });
            const formatted = formatOrdersForTableView(response, facilityTimezone);
            setAssociatedOrders(formatted);
            if (formatted.length > 1) {
                setOpenCloseAssociatedOrdersModal(true);
            } else {
                setShowCancelConfirm(true);
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to fetch associated orders');
        } finally {
            setCheckingAssociation(false);
        }
    };

    const confirmCancel = async () => {
        try {
            setCancelling(true);
            await cancelOrder(order.Facility, String(order._id), order.ResidentId);
            sendToast('success', 'Order canceled successfully');
            setShowCancelConfirm(false);
            setOpenCloseAssociatedOrdersModal(false);
            if (onCancelled) onCancelled();
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to cancel order');
        } finally {
            setCancelling(false);
        }
    };

    const cancelAllOpenAndAcceptedOrders = async () => {
        if (!associatedOrders || associatedOrders.length === 0) {
            sendToast('warn', `No request's to cancel`);
            return;
        }
        try {
            setCancelling(true);
            const results = await settleAll(
                associatedOrders.map((o) => cancelOrder(o.Facility, String(o._id), o.ResidentId)),
            );
            const succeeded = results.filter((r) => r.status === 'fulfilled').length;
            const failed = results.length - succeeded;
            setOpenCloseAssociatedOrdersModal(false);
            if (failed === 0) {
                sendToast('success', 'Order canceled successfully');
            } else if (succeeded > 0) {
                sendToast('warn', `${succeeded} of ${results.length} orders cancelled, ${failed} failed to cancel`);
            } else {
                sendToast('error', 'Failed to cancel orders');
            }
            // Refresh whenever at least one cancel actually landed, so the list
            // never keeps showing stale Open/Accepted status for orders that were
            // in fact closed on the backend.
            if (succeeded > 0 && onCancelled) onCancelled();
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to cancel order');
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div
            className={`order-card ${statusClass}`}
            style={{ ['--order-card-accent' as any]: deptColor }}
            onClick={() => onOpen(order)}
        >
            <div className="order-card-header">
                <div className="order-info">
                    <div className="order-name">{order.OrderName}</div>
                    <div className="order-meta">
                        <span>{order.Category || order.DepartmentName}</span>
                        {order?.Service?.name && (
                            <>
                                <span className="dot" />
                                <span>{order.Service.name}</span>
                            </>
                        )}
                        <span className="dot" />
                        <span>{order.DateTime}</span>
                    </div>
                </div>
                <div className={`status-pill ${statusClass}`}>
                    <div className="spulse" />
                    {STATUS_LABEL[order.Status] || order.Status}
                </div>
            </div>

            <div className="order-card-body">
                <div className="order-details">
                    <div className="order-detail">
                        <div className="detail-label">Submitted</div>
                        <div className="detail-val">
                            {order.DateAdded ? formatDateWithTZ(order.DateAdded, userFacingDateTimeFormat, facilityTimezone) : '-'}
                        </div>
                    </div>
                    {scheduled && (
                        <div className="order-detail">
                            <div className="detail-label">Scheduled</div>
                            <div className="detail-val">
                                {formatDateWithTZ(scheduled, userFacingDateTimeFormat, facilityTimezone)}
                            </div>
                        </div>
                    )}
                    {order.Description && (
                        <div className="order-detail" style={{ flex: 1 }}>
                            <div className="detail-label">Description</div>
                            <div className="detail-val">{order.Description}</div>
                        </div>
                    )}
                    {total > 0 && (
                        <div className="order-detail">
                            <div className="detail-label">Total</div>
                            <div className="detail-val" style={{ color: deptColor, fontWeight: 700 }}>
                                ${total.toFixed(2)}
                            </div>
                        </div>
                    )}
                    {acceptedByName && (
                        <div className="order-detail">
                            <div className="detail-label">Accepted By</div>
                            <div className="detail-val">
                                <span className="staff-pill accepted-by">
                                    <span className="staff-init accepted-by">{initialsOf(acceptedByName)}</span>
                                    {acceptedByName}
                                </span>
                            </div>
                        </div>
                    )}
                    {closedByLabel && (
                        <div className="order-detail">
                            <div className="detail-label">Closed By</div>
                            <div className="detail-val">
                                <span className="staff-pill closed-by">
                                    {!order.ClosedByResident && (
                                        <span className="staff-init closed-by">{initialsOf(closedByLabel)}</span>
                                    )}
                                    {closedByLabel}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                {(addons.length > 0 || externalCost > 0) && (
                    <div className="order-addons">
                        {addons.map((addon) => (
                            <span
                                key={addon.id}
                                className={`addon-tag${addon.itemCost ? ' priced' : ''}`}
                                style={addon.itemCost ? {
                                    background: `${deptColor}1a`,
                                    color: deptColor,
                                    borderColor: `${deptColor}55`,
                                } : undefined}
                            >
                                {addon.itemName}
                                {addon.itemCost ? ` +$${Number(addon.itemCost).toFixed(2)}` : ''}
                            </span>
                        ))}
                        {externalCost > 0 && (
                            <span
                                className="addon-tag priced"
                                style={{
                                    background: `${deptColor}1a`,
                                    color: deptColor,
                                    borderColor: `${deptColor}55`,
                                }}
                            >
                                External Cost +${externalCost.toFixed(2)}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="order-card-footer">
                <div className="footer-ref">Order #{getOrderNumber(order._id)}</div>
                <div className="footer-actions">
                    {canCancel && (
                        <button
                            type="button"
                            className="action-btn danger"
                            disabled={cancelling || checkingAssociation}
                            onClick={openCancelConfirm}
                        >
                            <Icon name="close" />
                            {cancelling ? 'Cancelling…' : checkingAssociation ? 'Checking…' : 'Cancel'}
                        </button>
                    )}
                    <button
                        type="button"
                        className="action-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpen(order);
                        }}
                    >
                        <Icon name="eye" />
                        Details
                    </button>
                </div>
            </div>

            {hasComments && (
                <div className="comments-section" onClick={(e) => e.stopPropagation()}>
                    <div className="comments-section-header">
                        <button
                            type="button"
                            className={`comments-toggle${commentsOpen ? ' open' : ''}`}
                            onClick={() => setCommentsOpen((prev) => !prev)}
                        >
                            <Icon name="comment outline" />
                            Comments
                            {commentCount !== null && <span className="ccount">{commentCount}</span>}
                            <Icon className="cchevron" name="chevron down" />
                        </button>
                    </div>
                    <div className={`comments-body${commentsOpen ? ' open' : ''}`}>
                        {commentsOpen && (
                            <OrderCommentsSection
                                commentsEnabled={true}
                                facilityId={activeResident.Facility}
                                requestInstanceId={String(order._id)}
                                registrantId={String(activeResident._id)}
                                facilityTimezone={facilityTimezone}
                                onCountChange={setCommentCount}
                                variant="card"
                            />
                        )}
                    </div>
                </div>
            )}

            <div onClick={(e) => e.stopPropagation()}>
                <DeleteConfirmationModal
                    open={showCancelConfirm}
                    onClose={() => setShowCancelConfirm(false)}
                    onConfirm={confirmCancel}
                    loading={cancelling}
                    headerText="Confirm Cancellation"
                    headerIcon="cancel"
                    confirmText="Cancel Order"
                    confirmColor="red"
                    dismissText="Keep Order"
                    messageToDisplay={`Are you sure you want to cancel this request - ${order.OrderName}?`}
                />
                <CloseAssociatedOrdersModal
                    open={openCloseAssociatedOrdersModal}
                    onClose={() => setOpenCloseAssociatedOrdersModal(false)}
                    cancelRequest={confirmCancel}
                    cancelAllOpenAndAcceptedOrders={cancelAllOpenAndAcceptedOrders}
                    associatedOrders={associatedOrders}
                    facilityTimezone={facilityTimezone}
                    loading={cancelling}
                />
            </div>
        </div>
    );
};

export default OrderStatusCard;
